import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';

const DEVICES = [
  { key: 'desktop', label: '🖥 Desktop', width: null },
  { key: 'tablet',  label: '📱 Tablet',  width: 768 },
  { key: 'mobile',  label: '📱 Mobile',  width: 375 },
];

export default function DraftPreview({ title, content, writerName, genreTree, tokenCost, onClose }) {
  const [device, setDevice] = useState('desktop');

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const deviceWidth = DEVICES.find(d => d.key === device)?.width;
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const genreLabel = genreTree?.main
    ? genreTree.main + (genreTree.sub ? ` / ${genreTree.sub.split(' › ').join(' / ')}` : '')
    : null;

  return (
    /* Outer shell — fixed, column flex, NO overflow here */
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, display: 'flex', flexDirection: 'column', backgroundColor: 'rgba(0,0,0,0.75)' }}>

      {/* ── Sticky control bar ── */}
      <div style={{
        flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0.75rem 1.25rem',
        backgroundColor: 'rgba(30,30,30,0.95)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
      }}>
        {/* Device toggles */}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {DEVICES.map(d => (
            <button
              key={d.key}
              onClick={() => setDevice(d.key)}
              style={{
                padding: '0.4rem 0.9rem',
                fontSize: '0.82rem',
                border: '1px solid',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: device === d.key ? '700' : '400',
                backgroundColor: device === d.key ? '#fff' : 'transparent',
                color: device === d.key ? '#1a1a1a' : '#ccc',
                borderColor: device === d.key ? '#fff' : '#555',
              }}
            >
              {d.label}
            </button>
          ))}
        </div>

        {/* Label + Close */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem' }}>
          <span style={{ color: '#aaa', fontSize: '0.78rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
            Draft Preview
          </span>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'none', border: 'none', color: '#aaa', fontSize: '1.6rem', cursor: 'pointer', lineHeight: 1, padding: '0 0.2rem' }}
          >
            ×
          </button>
        </div>
      </div>

      {/* ── Scrollable content area ── */}
      <div
        style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '1.5rem 1rem 3rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
        onClick={onClose}
      >
        {/* Article card */}
        <div
          style={{
            width: deviceWidth ? `${deviceWidth}px` : '100%',
            maxWidth: deviceWidth ? `${deviceWidth}px` : '900px',
            backgroundColor: '#fff',
            borderRadius: '12px',
            boxShadow: '0 12px 48px rgba(0,0,0,0.4)',
            transition: 'width 0.25s ease, max-width 0.25s ease',
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Phone notch bar */}
          {device !== 'desktop' && (
            <div style={{ backgroundColor: '#1a1a1a', padding: '0.4rem', display: 'flex', justifyContent: 'center' }}>
              <div style={{ width: 40, height: 5, borderRadius: 3, backgroundColor: '#555' }} />
            </div>
          )}

          <div style={{ padding: device === 'mobile' ? '1.25rem' : '2rem 2.5rem 2.5rem' }}>

            {/* Genre breadcrumb — matches live site */}
            {genreLabel && (
              <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '0.5rem', fontWeight: '600', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                {genreLabel}
              </div>
            )}

            {/* Title */}
            <h1 style={{
              fontSize: device === 'mobile' ? '1.5rem' : '2rem',
              fontWeight: '800', color: '#1a1a1a', lineHeight: 1.2,
              marginBottom: '0.75rem',
            }}>
              {title || <span style={{ color: '#bbb' }}>Untitled Article</span>}
            </h1>

            {/* Meta — exactly matching live CompanyPage format */}
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: '1rem',
              fontSize: '0.85rem', color: '#666',
              marginBottom: '1.5rem', paddingBottom: '1rem',
              borderBottom: '1px solid #f0f0f0',
              alignItems: 'center',
            }}>
              <span>Written By: <strong>{writerName ? writerName.split(',')[0].trim() : 'Author'}</strong></span>
              <span>Published: {today} <em style={{ color: '#bbb', fontWeight: 400 }}>(draft)</em></span>
            </div>

            {/* Content */}
            <div
              className="draft-preview-content"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(content || '<p style="color:#bbb;font-style:italic">No content yet.</p>', {
                  ADD_TAGS: ['iframe'],
                  ADD_ATTR: ['allowfullscreen', 'frameborder', 'allow'],
                }),
              }}
            />

            {/* Purchase button preview */}
            {tokenCost != null && tokenCost !== '' && tokenCost !== 0 && (
              <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #f0f0f0' }}>
                <button style={{
                  padding: '0.65rem 1.5rem', backgroundColor: '#343a40', color: '#fff',
                  border: 'none', borderRadius: '8px', fontSize: '0.9rem',
                  fontWeight: '600', cursor: 'default', opacity: 0.85,
                }}>
                  Purchase · {tokenCost} tokens
                </button>
                <span style={{ marginLeft: '0.75rem', fontSize: '0.78rem', color: '#bbb' }}>(preview only)</span>
              </div>
            )}
          </div>

          {/* Phone home bar */}
          {device !== 'desktop' && (
            <div style={{ backgroundColor: '#1a1a1a', padding: '0.5rem', display: 'flex', justifyContent: 'center' }}>
              <div style={{ width: 60, height: 4, borderRadius: 3, backgroundColor: '#444' }} />
            </div>
          )}
        </div>
      </div>

      <style>{`
        .draft-preview-content { font-size: 0.95rem; line-height: 1.75; color: #2c2c2c; word-break: break-word; }
        .draft-preview-content p { margin-bottom: 0.85rem; }
        .draft-preview-content h1 { font-size: 1.6rem; font-weight: 800; margin: 1rem 0 0.5rem; }
        .draft-preview-content h2 { font-size: 1.3rem; font-weight: 700; margin: 0.85rem 0 0.4rem; }
        .draft-preview-content h3 { font-size: 1.1rem; font-weight: 600; margin: 0.75rem 0 0.35rem; }
        .draft-preview-content ul, .draft-preview-content ol { padding-left: 1.5rem; margin-bottom: 0.85rem; }
        .draft-preview-content blockquote { border-left: 3px solid #ccc; margin: 0.75rem 0; padding-left: 1rem; color: #555; font-style: italic; }
        .draft-preview-content pre { background: #f4f4f4; border-radius: 6px; padding: 0.75rem 1rem; font-family: monospace; font-size: 0.88rem; overflow-x: auto; }
        .draft-preview-content img { max-width: 100%; border-radius: 6px; margin: 0.5rem 0 0.15rem; display: block; }
        .draft-preview-content figure { margin: 1rem 0; }
        .draft-preview-content figure img { margin-bottom: 0.25rem; }
        .draft-preview-content figcaption { font-size: 0.82rem; color: #666; font-style: italic; padding: 0.2rem 0.5rem; border-left: 2px solid #ddd; }
        .draft-preview-content p.img-caption { font-size: 0.82rem; color: #666; font-style: italic; margin: 0 0 0.85rem; padding: 0.25rem 0.5rem; border-left: 2px solid #ddd; }
        .draft-preview-content iframe { max-width: 100%; border-radius: 6px; margin: 0.5rem 0; }
        .draft-preview-content .tableWrapper, .draft-preview-content div[style*="overflow-x"] { overflow-x: auto; max-width: 100%; }
        .draft-preview-content table { border-collapse: collapse; width: 100%; margin: 0.8rem 0; font-size: 0.9rem; }
        .draft-preview-content td, .draft-preview-content th { border: 1px solid #d1d5db; padding: 7px 11px; vertical-align: top; word-break: break-word; }
        .draft-preview-content th { background: #f3f4f6; font-weight: 600; text-align: left; }
        .draft-preview-content a { color: #3366cc; text-decoration: underline; }
        .draft-preview-content strong { font-weight: 700; }
        .draft-preview-content em { font-style: italic; }
        .draft-preview-content u { text-decoration: underline; }
        .draft-preview-content s { text-decoration: line-through; }
        .draft-preview-content code { font-family: monospace; background: #f4f4f4; padding: 0.1em 0.3em; border-radius: 3px; }
      `}</style>
    </div>
  );
}
